import type { CSSProperties, ReactNode } from "react";
import {
  type SaudiSourceMarketRow,
  useGlobals,
  useSaudiSourceMarkets,
  useSourceMarkets,
} from "../../lib/dashboard-api";
import { preloadWorkspace, sectionPreloader } from "../../lib/section-preload";
import { APP_NAME, COUNTRY_ADJ, COUNTRY_ISO3, HERO_DESTINATION } from "../../lib/tenant";
import { ThemeToggle, useTheme } from "../../lib/theme";
import { useMediaQuery } from "../../lib/use-breakpoint";
import { Flag } from "./flag";
import { C, FM, FS, INK_LIGHT, INK as INK_T, accentInk } from "./tokens";
import { type WorkspaceId, workspaceAvailable } from "./workspaces";

/**
 * Landing screen — the full-bleed "demand sensing agent" splash the app opens
 * on. Six numbered sections on a 3-column grid (two rows of three), every card
 * the same size.
 *
 * Pixel-matched to the approved STA demo mockup (2026-07-07 "remixed" rev):
 * each card now carries a LARGE stage visual ported from the prototype —
 * KPI chips + sparklines (01), a live signal stream + platform icon stack
 * (02), the 8-archetype grid (03), the flag-ranked source-market list (04),
 * the segment×product match heatmap (05), and the GTM pillar strip + ROI
 * simulator ring (06). All figures inside the stages are ILLUSTRATIVE demo
 * values from the signed-off mockup, not live data.
 *
 * The screen honours the app's light/dark theme (toggle top-right). Card →
 * destination mapping is per Ohoud's sign-off:
 *   01 Intelligence Dashboard          → Intelligence workspace
 *   02 Demand Signals                  → Demand Signals workspace
 *   03 Traveler Behaviour & Segm.      → Segmentation workspace
 *   04 Market Prioritisation           → Prioritization workspace
 *   05 Product Matching & Gap Analysis → Product Matching workspace
 *   06 The go-to-market plan           → Go-to-Market workspace
 */

/** Where a card opens: a whole workspace, a deep-linked subsection, or nothing
 *  yet (placeholder). */
type NavTarget = { workspace: WorkspaceId } | { section: string } | null;

function preloadTarget(target: NavTarget): void {
  if (!target) return;
  if ("workspace" in target) preloadWorkspace(target.workspace);
  else sectionPreloader.now(target.section);
}

/* This screen IS the type reference the app-wide ramp (tokens.ts TYPE/INK) was
   derived from — consume the tokens so reference and system can't drift. */
const FSERIF = FS;
const INK = INK_T.hi;
const DIM = INK_T.md;
const FAINT = INK_T.faint;

/* Stage palette — the landing's own dark-world accents (mapped from the
   prototype's cobalt/gold/green/purple/pink/finale hues). */
const COBALT = "#82BAFF";
const GOLD = "#E9C977";
const GREEN = "#5DD4A4";
const PURPLE = "#A89BFF";
const PINK = "#FF6B9D";
const AMBER = "#FFA552";
const YELLOW = "#FFD93D";

// A hue read as TEXT needs its darker twin on the light tile (a pale gold/green is
// invisible on a light inset); strokes and box fills keep the raw hue.
const htwin = (c: string, light: boolean) => (light ? accentInk(c, true) : c);

type Stage = "intel" | "stream" | "archetypes" | "rank" | "heat" | "sim";

interface Section {
  n: string;
  /** White part of the title. */
  title: string;
  /** Italic, accent-coloured trailing word(s). */
  accentWord: string;
  accent: string;
  sub: string;
  target: NavTarget;
  stage: Stage;
}

const ALL_SECTIONS: Section[] = [
  {
    n: "01",
    title: "Intelligence",
    accentWord: "Dashboard",
    accent: COBALT,
    sub: "A live view of tourism.",
    target: { workspace: "intelligence" },
    stage: "intel",
  },
  {
    n: "02",
    title: "Demand",
    accentWord: "Signals",
    accent: C.coral,
    sub: "Millions of traveler data from social scraping.",
    target: { workspace: "demand-signals" },
    stage: "stream",
  },
  {
    n: "03",
    title: "Traveler Behaviour &",
    accentWord: "Segmentation",
    accent: GREEN,
    sub: "Distinct segments resolved from the signal base.",
    target: { workspace: "segmentation" },
    stage: "archetypes",
  },
  {
    n: "04",
    title: "Market Prioritisation &",
    accentWord: "Deep Dive",
    accent: PURPLE,
    sub: "Where to play.",
    target: { workspace: "prioritization" },
    stage: "rank",
  },
  {
    n: "05",
    title: "Product Matching &",
    accentWord: "Gap Analysis",
    accent: PINK,
    sub: "What to sell, and what to build.",
    target: { workspace: "product" },
    stage: "heat",
  },
  {
    n: "06",
    title: "The",
    accentWord: "go-to-market plan",
    accent: "#FFC02E",
    sub: "Demand · Offering · Marketing · Access - plus simulator.",
    target: { workspace: "gtm" },
    stage: "sim",
  },
];

/* A card is a door into a workspace, so it only exists when that workspace does
   — the demo deployment withholds Segmentation (see workspaces.ts), and a card
   that navigated nowhere would be the most visible bug on the front page. The
   numbering is the registry's own, so the remaining cards keep the numbers the
   client's deck uses rather than silently renumbering. */
const SECTIONS: Section[] = ALL_SECTIONS.filter(
  (s) => !(s.target && "workspace" in s.target) || workspaceAvailable(s.target.workspace),
);

/* Deterministic scatter of faint background particles (no Math.random so SSR /
   re-renders stay stable). [left%, top%, size px, opacity]. Thinned from 25 to
   a calmer, lower-opacity set so it reads as faint atmosphere, not a starfield
   competing with the cards. */
const DOTS: [number, number, number, number][] = [
  [8, 18, 2, 0.22],
  [22, 30, 1.5, 0.18],
  [38, 12, 2, 0.22],
  [64, 66, 1.5, 0.18],
  [78, 74, 2, 0.2],
  [88, 24, 1.5, 0.16],
  [49, 84, 1.5, 0.18],
  [93, 52, 1.5, 0.16],
];

export function LandingHub({ onOpen }: { onOpen: (t: NavTarget) => void }) {
  const [theme, setTheme] = useTheme();
  const isLight = theme === "light";
  const ink = isLight ? INK_LIGHT : INK_T;
  // Below 640px three columns can't hold a card that carries a stage visual, so
  // the grid collapses to one stacked column (and the page padding tightens).
  // NB the 641–899px band ALSO renders one column, but not from here: app.css's
  // ≤899px rule collapses any inline `repeat(3, 1fr)` grid, which this one is.
  // So `stack` only still governs the padding + minWidth in that band.
  const stack = useMediaQuery("(max-width: 640px)");
  return (
    <div
      style={
        {
          position: "relative",
          minHeight: "100vh",
          width: "100%",
          background: isLight
            ? "radial-gradient(125% 90% at 50% -10%, #eef2f9 0%, #f4f7fc 42%, #f7f9fc 100%)"
            : "radial-gradient(125% 90% at 50% -10%, #18223e 0%, #0c1124 42%, #080b16 100%)",
          overflow: "hidden",
          padding: "16px 0 12px",
          boxSizing: "border-box",
          // Mini-preview tiles follow the theme: a light inset on the light page,
          // the exact translucent dark on the dark page.
          "--lh-tile-bg": isLight ? "#eef2f9" : "rgba(0,0,0,0.34)",
        } as CSSProperties
      }
    >
      <style>{`
        /* Smallest phones only (<=374px): cards 01 (global/Saudi) and 02 (stream +
           platform stack) carry two side-by-side preview tiles whose combined
           min-width (~339px) can't fit the card, so the 2nd tile clips past the
           shell's overflow-x:clip edge. Below 374px stack the tiles vertically so
           each spans the card full-width. Phones 375px and up (incl. iPhone SE)
           keep the approved side-by-side mockup exactly. */
        @media (max-width: 374px) {
          .lh-split { flex-direction: column; height: auto !important; }
          .lh-split > * { flex: 0 0 auto !important; width: 100% !important; min-width: 0; }
          .lh-iconstack { flex-direction: row !important; justify-content: center; padding: 6px 0 0; }
        }
      `}</style>
      {/* particle field */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {DOTS.map(([l, t, s, o]) => (
          <span
            key={`${l}-${t}`}
            style={{
              position: "absolute",
              left: `${l}%`,
              top: `${t}%`,
              width: s,
              height: s,
              borderRadius: "50%",
              background: isLight ? "#8fa0c4" : "#9fb4e0",
              opacity: o,
            }}
          />
        ))}
      </div>

      <div style={{ position: "relative", maxWidth: 1440, margin: "0 auto", padding: stack ? "0 16px" : "0 28px" }}>
        {/* ── top bar ─────────────────────────────────────────────────── */}
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: FM, fontSize: 11, letterSpacing: "0.22em", color: ink.md }}>
            <span style={{ color: ink.hi, fontWeight: 600, textTransform: "uppercase" }}>{APP_NAME}</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 10, color: ink.hi }}>
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </span>
        </header>

        {/* ── hero ──────────────────────────────────────────────────────
             The old centered "SAUDI TOURISM AUTHORITY · DEMO" eyebrow was
             dropped — it duplicated the top-left "STA · DEMAND SENSING AGENT"
             and cost a row of height the no-scroll board can't spare. */}
        <div style={{ textAlign: "center", margin: "18px 0 18px" }}>
          <h1
            style={{
              fontFamily: FSERIF,
              fontWeight: 300,
              fontSize: 43,
              lineHeight: 1.05,
              color: ink.hi,
              margin: 0,
            }}
          >
            A demand sensing agent for{" "}
            <em style={{ fontStyle: "italic", color: isLight ? C.goldText : C.goldSerif }}>{COUNTRY_ADJ} tourism</em>
          </h1>
          <p
            style={{
              fontFamily: FSERIF,
              fontStyle: "italic",
              fontSize: 15,
              color: ink.md,
              margin: "12px 0 0",
            }}
          >
            From millions of signals into structured go-to-market plans.
          </p>
        </div>

        {/* ── grid ────────────────────────────────────────────────────────
             Six sections on one 3-column grid — two rows of three, every card
             the same size. (The earlier 2 / 3 / 1 funnel made 06 a narrow
             odd-one-out and cost a third row of vertical scroll.) */}
        {/* gridAutoRows: 1fr forces BOTH rows to the same height, so all six
            cards are identical — the precondition for identical inner visuals. */}
        <div
          style={{
            display: "grid",
            // minmax(0, 1fr) — not bare 1fr (= minmax(auto,1fr)), whose `auto`
            // floor lets a column with wide min-content (card 01's KPI tiles)
            // steal width and make the tracks unequal. This forces equal thirds.
            gridTemplateColumns: stack ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gridAutoRows: stack ? "auto" : "1fr",
            gap: 16,
          }}
        >
          {SECTIONS.map((s) => (
            <Card key={s.n} section={s} onOpen={onOpen} isLight={isLight} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── card ──────────────────────────────────────────────────────────────── */
function Card({
  section,
  onOpen,
  isLight,
}: {
  section: Section;
  onOpen: (t: NavTarget) => void;
  isLight: boolean;
}) {
  const { n, title, accentWord, accent, sub, stage, target } = section;
  // A null target = placeholder ("empty for now"): render as a non-interactive
  // div, no click, no hover lift, and a muted footer — but otherwise identical
  // so the funnel keeps its six-card composition.
  const enabled = target != null;
  const ink = isLight ? INK_LIGHT : INK_T;
  // Legible accent on light (darkened toward navy, hue kept); raw bright on dark.
  const ac = isLight ? accentInk(accent, true) : accent;
  // Card chrome per theme — dark keeps its EXACT translucent literals (byte-identical).
  const cardBg = isLight ? "#ffffff" : "linear-gradient(180deg, rgba(255,255,255,0.022), rgba(255,255,255,0.006))";
  const restBorderColor = isLight ? "#e3e9f3" : "rgba(140,170,220,0.13)";
  const restShadow = isLight ? "0 1px 2px rgba(16,24,52,0.05)" : "0 1px 0 rgba(255,255,255,0.02) inset";
  const topBorderColor = isLight ? ac : hexA(accent, 0.55);
  const hoverBorderColor = isLight ? `color-mix(in srgb, ${ac} 55%, transparent)` : hexA(accent, 0.5);
  const hoverShadow = isLight
    ? `0 8px 26px color-mix(in srgb, ${ac} 16%, transparent)`
    : `0 8px 30px ${hexA(accent, 0.1)}`;
  return (
    <button
      type="button"
      disabled={!enabled}
      onClick={enabled ? () => onOpen(target) : undefined}
      onPointerEnter={() => preloadTarget(target)}
      onFocus={() => preloadTarget(target)}
      style={{
        position: "relative",
        textAlign: "left",
        cursor: enabled ? "pointer" : "default",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "15px 18px 15px",
        borderRadius: 10,
        border: `1px solid ${restBorderColor}`,
        borderTop: `1px solid ${topBorderColor}`,
        background: cardBg,
        boxShadow: restShadow,
        transition: "border-color 140ms ease, transform 140ms ease, box-shadow 140ms ease",
      }}
      onMouseEnter={
        enabled
          ? (e) => {
              const el = e.currentTarget;
              el.style.borderColor = hoverBorderColor;
              el.style.transform = "translateY(-2px)";
              el.style.boxShadow = hoverShadow;
            }
          : undefined
      }
      onMouseLeave={
        enabled
          ? (e) => {
              const el = e.currentTarget;
              el.style.borderColor = restBorderColor;
              el.style.transform = "translateY(0)";
              el.style.boxShadow = restShadow;
            }
          : undefined
      }
    >
      {/* title row — reserve the two-line max height so a card whose title wraps
          (03/04/05) doesn't steal height from its visual. Shorter titles keep the
          negative space; every visual below therefore starts at the same y and
          comes out identical. Pairs with the two-line reservation on the sub. */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, minHeight: 49 }}>
        <span style={{ fontFamily: FSERIF, fontStyle: "italic", fontSize: 27, color: ac, lineHeight: 1 }}>{n}</span>
        <h2 style={{ fontFamily: FSERIF, fontWeight: 300, fontSize: 19, color: ink.hi, margin: 0, lineHeight: 1.15 }}>
          {title} <em style={{ fontStyle: "italic", color: ac }}>{accentWord}</em>
        </h2>
      </div>
      {/* Reserve a constant two-line height for the sub so a card whose sub wraps
          (06) doesn't steal height from its visual — every header is the same
          height, so every flex visual below comes out identical. */}
      <p
        style={{
          fontFamily: FM,
          fontSize: 11,
          lineHeight: 1.35,
          letterSpacing: "0.01em",
          color: ink.md,
          margin: "-6px 0 0",
          minHeight: 30,
        }}
      >
        {sub}
      </p>

      {/* stage visual — a flex column so the tile inside (MiniShell / .lh-split)
          FILLS this area instead of sizing to its SVG content, which is what made
          the wider-viewBox tiles (05, 06) render shorter than the rest. */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <StageViz stage={stage} />
      </div>

      {/* footer */}
      <span
        style={{
          fontFamily: FM,
          fontSize: 10.5,
          letterSpacing: "0.16em",
          color: enabled ? ac : ink.faint,
        }}
      >
        {enabled ? "OPEN DETAIL →" : "COMING SOON"}
      </span>
    </button>
  );
}

/* ── stage shells ──────────────────────────────────────────────────────── */

/** Dark inner tile: header (icon + mono label) above a full-width canvas. */
function MiniShell({
  label,
  labelColor,
  icon,
  children,
}: {
  label: string;
  labelColor?: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  const [theme] = useTheme();
  const L = theme === "light";
  // Hue labels get their legible light twin on light; the SVG mock inks flip via
  // the CSS vars declared here (--mk-ink / --mk-dim), read by every stage below.
  const labelC = labelColor ? (L ? accentInk(labelColor, true) : labelColor) : L ? INK_LIGHT.faint : FAINT;
  return (
    <div
      style={
        {
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "10px 12px 9px",
          borderRadius: 7,
          background: "var(--lh-tile-bg, rgba(0,0,0,0.34))",
          border: `1px solid ${L ? "#dbe2ee" : "rgba(255,255,255,0.055)"}`,
          overflow: "hidden",
          "--mk-ink": L ? INK_LIGHT.hi : INK_T.hi,
          "--mk-dim": L ? INK_LIGHT.md : INK_T.md,
        } as CSSProperties
      }
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 15 }}>
        {icon}
        <span
          style={{
            fontFamily: FM,
            fontSize: 9.5,
            letterSpacing: "0.13em",
            color: labelC,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

/** Full-width responsive SVG canvas. */
function Canvas({ vb, children }: { vb: string; children: ReactNode }) {
  return (
    <svg
      viewBox={vb}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      {children}
    </svg>
  );
}

function StageViz({ stage }: { stage: Stage }) {
  switch (stage) {
    case "intel":
      return <IntelStage />;
    case "stream":
      return <StreamStage />;
    case "archetypes":
      return <ArchetypesStage />;
    case "rank":
      return <RankStage />;
    case "heat":
      return <HeatStage />;
    case "sim":
      return <SimStage />;
  }
}

/* ── 01 · Intelligence — global + Saudi KPI panels with sparklines ─────── */

function KpiChips({ color, chips }: { color: string; chips: Array<[string, string]> }) {
  const xs = [2, 54, 106];
  const ws = [48, 48, 52];
  const [theme] = useTheme();
  const L = theme === "light";
  return (
    <g fontFamily={FM} letterSpacing="0.4">
      {chips.map(([label, value], i) => (
        <g key={label}>
          <rect x={xs[i]} y={2} width={ws[i]} height={22} fill={hexA(color, 0.1)} stroke={color} strokeWidth={0.5} />
          <text x={xs[i]! + 4} y={11} fontSize={5.5} fill={htwin(color, L)}>
            {label}
          </text>
          <text x={xs[i]! + 4} y={20.5} fontFamily={FSERIF} fontStyle="italic" fontSize={9.5} fill="var(--mk-ink)">
            {value}
          </text>
        </g>
      ))}
    </g>
  );
}

function SparkArea({ color, pts }: { color: string; pts: string }) {
  return (
    <g transform="translate(2 30)">
      <line x1={0} y1={42} x2={156} y2={42} stroke={color} strokeWidth={0.3} opacity={0.25} />
      <path d={`M ${pts}`} fill="none" stroke={color} strokeWidth={1.2} />
      <path d={`M ${pts} L 156 42 L 0 42 Z`} fill={color} opacity={0.1} />
    </g>
  );
}

/* The one stage on this screen whose chips are HEADLINE KPIs rather than
 * texture: card 01 is the Intelligence dashboard, and these six numbers are the
 * same six the workspace opens on. They were literals from the signed-off
 * mockup, which put the landing screen roughly 3× out of step with the section
 * it links to on the demo tenant, and stale on the client's own figures as
 * well — including a market count the source-market repo has never produced.
 * (The exact stale values are pinned in `__tests__/tenant-copy.test.ts` rather
 * than repeated here, so the guard there is a real signal and not a match on
 * this comment.)
 *
 * Live, from the same two routes the sections read, so the two screens cannot
 * drift again. Em-dashes while the queries are in flight, never a placeholder
 * number — the landing must not show a figure it cannot source.
 *
 * The sparklines stay illustrative: they are shape, not a claim, and the routes
 * behind these tiles carry no comparable series. Every OTHER stage on this
 * screen is texture and is left as the mockup drew it. */
const DASH = "—";

function IntelStage() {
  const globals = useGlobals();
  const world = useSourceMarkets();
  const focus = useSaudiSourceMarkets();

  const arrivals = globals.data?.hero?.arrivals.value ?? null;
  const receipts = globals.data?.hero?.receipts.value ?? null;
  const marketCount = world.data?.countries.length ?? null;

  // `arrivals`/`spend` are millions and $bn respectively — the units the Saudi
  // source-market route publishes and 1.2 renders.
  const rows = focus.data?.markets ?? [];
  const sum = (pick: (m: SaudiSourceMarketRow) => number) => rows.reduce((s, m) => s + pick(m), 0);
  const visitorsM = rows.length ? sum((m) => m.arrivals) : null;
  const spendB = rows.length ? sum((m) => m.spend) : null;
  const base = rows.length ? sum((m) => m.arrivals2019) : 0;
  const growthPct = visitorsM != null && base > 0 ? (visitorsM / base - 1) * 100 : null;

  return (
    <div className="lh-split" style={{ display: "flex", gap: 10, height: "100%" }}>
      <MiniShell label="GLOBAL TOURISM" icon={<GlobeIcon />}>
        <Canvas vb="0 0 160 76">
          <KpiChips
            color={COBALT}
            chips={[
              ["VISITORS", arrivals == null ? DASH : `${(arrivals / 1000).toFixed(1)}B`],
              ["SPEND", receipts == null ? DASH : `$${(receipts / 1000).toFixed(1)}T`],
              ["MARKETS", marketCount == null ? DASH : String(marketCount)],
            ]}
          />
          <SparkArea
            color={COBALT}
            pts="0 34 L 14 30 L 28 24 L 42 28 L 56 18 L 70 14 L 84 20 L 98 16 L 112 10 L 126 12 L 140 16 L 156 8"
          />
          <circle cx={158} cy={38} r={1.6} fill={COBALT} />
        </Canvas>
      </MiniShell>
      <MiniShell label={`${COUNTRY_ADJ.toUpperCase()} TOURISM`} icon={<FocusFlag />}>
        <Canvas vb="0 0 160 76">
          <KpiChips
            color={GREEN}
            chips={[
              ["VISITORS", visitorsM == null ? DASH : `${visitorsM.toFixed(1)}M`],
              ["SPEND", spendB == null ? DASH : `$${spendB.toFixed(1)}B`],
              ["GROWTH", growthPct == null ? DASH : `${growthPct >= 0 ? "+" : ""}${Math.round(growthPct)}%`],
            ]}
          />
          <SparkArea
            color={GREEN}
            pts="0 38 L 14 36 L 28 32 L 42 30 L 56 24 L 70 20 L 84 18 L 98 14 L 112 10 L 126 8 L 140 6 L 156 4"
          />
          <circle cx={158} cy={34} r={1.6} fill={GREEN} />
        </Canvas>
      </MiniShell>
    </div>
  );
}

/* ── 02 · Demand signals — live stream + platform stack ────────────────── */

function StreamStage() {
  const [theme] = useTheme();
  const L = theme === "light";
  return (
    <div className="lh-split" style={{ display: "flex", gap: 10, height: "100%" }}>
      <MiniShell label="LIVE SIGNAL STREAM" labelColor={GOLD}>
        <Canvas vb="0 0 300 76">
          <g fontFamily={FM} letterSpacing="0.5">
            <text x={4} y={7} fontSize={5} fill={htwin(GOLD, L)}>
              SIGNAL VOLUME · LAST 24H
            </text>
            <text x={4} y={24} fontFamily={FSERIF} fontStyle="italic" fontSize={17} fill="var(--mk-ink)">
              2.4M
            </text>
            <text x={54} y={24} fontSize={6.5} fill={htwin(GREEN, L)}>
              +18%
            </text>
            <text x={4} y={33} fontSize={4.8} fill="var(--mk-dim)">
              posts · comments · reviews
            </text>
          </g>
          <g transform="translate(4 40)">
            <path
              d="M 0 18 L 10 18 L 14 10 L 22 26 L 30 8 L 38 22 L 46 14 L 54 24 L 62 12 L 72 16 L 84 8 L 96 22 L 108 12 L 120 20 L 132 6 L 144 24 L 156 14 L 168 22 L 182 10 L 196 18 L 210 14 L 224 22 L 240 16 L 254 20 L 268 10 L 280 18 L 292 14"
              fill="none"
              stroke={GOLD}
              strokeWidth={1.2}
              opacity={0.9}
            />
            <line x1={0} y1={30} x2={292} y2={30} stroke={GOLD} strokeWidth={0.3} opacity={0.3} />
          </g>
          <text x={4} y={75} fontFamily={FM} fontSize={5} fill="var(--mk-dim)" letterSpacing="0.5">
            63 STRUCTURED VARIABLES · 11 SECTIONS
          </text>
        </Canvas>
      </MiniShell>
      {/* platform stack */}
      <div
        className="lh-iconstack"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          flex: "0 0 auto",
          padding: "3px 0",
          gap: 6,
        }}
      >
        <InstagramIcon />
        <RedditIcon />
        <TripadvisorIcon />
        <XIcon />
      </div>
    </div>
  );
}

/* ── 03 · Segmentation — 8 global archetypes ───────────────────────────── */

const ARCHETYPES: Array<{ name: string; desc: string; pct: string; color: string; dim?: boolean }> = [
  { name: "SUN & SEA", desc: "Beach · repeat", pct: "14.6%", color: AMBER },
  { name: "CULTURE", desc: "Authentic", pct: "11.3%", color: PURPLE },
  { name: "STRATEGIC", desc: "Points-led", pct: "9.2%", color: GREEN },
  { name: "TRENDS", desc: "Status", pct: "13.8%", color: PINK },
  { name: "COST", desc: "Price-led", pct: "17.2%", color: YELLOW },
  { name: "PREMIUM", desc: "Luxury", pct: "8.1%", color: COBALT },
  { name: "ADVENT.", desc: "Off-grid", pct: "7.6%", color: GREEN, dim: true },
  { name: "BLEISURE", desc: "Biz+leisure", pct: "18.2%", color: PURPLE, dim: true },
];

function ArchetypesStage() {
  const [theme] = useTheme();
  const L = theme === "light";
  return (
    <MiniShell label="8 GLOBAL ARCHETYPES" labelColor={GREEN}>
      <Canvas vb="0 0 280 100">
        <g fontFamily={FM} fontSize={5.5} letterSpacing="0.4">
          {ARCHETYPES.map((a, i) => {
            const x = (i % 4) * 69 + 2;
            const y = Math.floor(i / 4) * 48 + 4;
            const w = i % 4 === 3 ? 67 : 66;
            return (
              <g key={a.name} transform={`translate(${x} ${y})`} opacity={a.dim ? 0.85 : 1}>
                <rect width={w} height={44} rx={2} fill={hexA(a.color, 0.1)} stroke={a.color} strokeWidth={0.6} />
                <circle cx={11} cy={14} r={5} fill={a.color} opacity={0.9} />
                <path d="M 4 28 Q 11 22 18 28 L 18 32 L 4 32 Z" fill={a.color} opacity={0.65} />
                <text x={22} y={15} fill="var(--mk-ink)">
                  {a.name}
                </text>
                <text x={22} y={23} fill="var(--mk-dim)" fontSize={4.8}>
                  {a.desc}
                </text>
                <text x={22} y={36} fontFamily={FSERIF} fontStyle="italic" fontSize={10} fill={htwin(a.color, L)}>
                  {a.pct}
                </text>
              </g>
            );
          })}
        </g>
      </Canvas>
    </MiniShell>
  );
}

/* ── 04 · Prioritisation — ranked source markets with flags ────────────── */

const RANKED: Array<{ rank: string; flag: FlagKind; code: string; w: number; delta?: string; dim?: boolean }> = [
  { rank: "01", flag: "uk", code: "UK", w: 180, delta: "+24%" },
  { rank: "02", flag: "de", code: "DE", w: 158, delta: "+18%" },
  { rank: "03", flag: "us", code: "US", w: 140, delta: "+12%" },
  { rank: "04", flag: "fr", code: "FR", w: 124, delta: "+9%" },
  { rank: "05", flag: "in", code: "IN", w: 104, delta: "+38%" },
  { rank: "06", flag: "cn", code: "CN", w: 88, delta: "+27%" },
  { rank: "07", flag: "it", code: "IT", w: 64, dim: true },
  { rank: "08", flag: "jp", code: "JP", w: 48, dim: true },
];

function RankStage() {
  const [theme] = useTheme();
  const L = theme === "light";
  return (
    <MiniShell label="SOURCE MARKETS · RANKED" labelColor={PURPLE}>
      <Canvas vb="0 0 280 100">
        {RANKED.map((r, i) => {
          const y = r.dim ? 76 + (i - 6) * 10 : 4 + i * 10;
          return (
            <g
              key={r.code}
              transform={`translate(2 ${y})`}
              fontFamily={FM}
              fontSize={5.8}
              letterSpacing="0.4"
              opacity={r.dim ? 0.45 : 1}
            >
              <text x={0} y={6} fill={htwin(PURPLE, L)}>
                {r.rank}
              </text>
              <FlagG kind={r.flag} x={11} y={-1} w={14} h={9} />
              <text x={28} y={6} fill={r.dim ? "var(--mk-dim)" : "var(--mk-ink)"}>
                {r.code}
              </text>
              <rect x={44} y={2} width={r.w} height={5} rx={0.5} fill={PURPLE} opacity={0.9 - i * 0.07} />
              {r.delta && (
                <text x={244} y={6} fill={htwin(GREEN, L)}>
                  {r.delta}
                </text>
              )}
            </g>
          );
        })}
        <line x1={2} y1={68} x2={278} y2={68} stroke={GOLD} strokeWidth={0.5} strokeDasharray="2 2" opacity={0.7} />
        <rect x={104} y={63.5} width={76} height={9.5} rx={0.5} fill="#0c1124" stroke={GOLD} strokeWidth={0.4} />
        <text x={142} y={70.2} textAnchor="middle" fontFamily={FM} fontSize={5.2} fill={GOLD} letterSpacing="0.6">
          PRIORITY · TOP 6
        </text>
      </Canvas>
    </MiniShell>
  );
}

/* ── 05 · Product matching — segment × product heatmap ─────────────────── */

const HEAT_COLS = ["LUX", "CULT", "ADV", "SUN", "WELL", "FOOD"];
const HEAT_ROWS: Array<{ label: string; cells: number[]; score?: [number, string] }> = [
  { label: "PRM", cells: [0.95, 0.55, 0.3, 0.45, 0.75, 0.65], score: [0, "92"] },
  { label: "TRD", cells: [0.55, 0.85, 0.65, 0.45, 0.55, 0.85], score: [1, "81"] },
  { label: "CUL", cells: [0.3, 0.95, 0.4, 0.2, 0.6, 0.75], score: [1, "94"] },
  { label: "ADV", cells: [0.2, 0.55, 0.95, 0.4, 0.7, 0.45], score: [2, "93"] },
  { label: "BLE", cells: [0.65, 0.5, 0.3, 0.55, 0.7, 0.6] },
  { label: "CST", cells: [0.1, 0.55, 0.4, 0.85, 0.35, 0.3], score: [3, "85"] },
];

function HeatStage() {
  const [theme] = useTheme();
  const L = theme === "light";
  return (
    <MiniShell label="MATCH SCORE · SEGMENT × PRODUCT" labelColor={PINK}>
      <Canvas vb="0 0 280 100">
        <g fontFamily={FM} fontSize={5} fill="var(--mk-dim)" letterSpacing="0.5">
          {HEAT_COLS.map((c, i) => (
            <text key={c} x={48 + i * 36 + 4} y={9}>
              {c}
            </text>
          ))}
        </g>
        <g fontFamily={FM} fontSize={5.5} letterSpacing="0.3">
          {HEAT_ROWS.map((row, ri) => {
            const y = 12 + ri * 13;
            return (
              <g key={row.label}>
                <text x={2} y={y + 8} fill="var(--mk-ink)">
                  {row.label}
                </text>
                {row.cells.map((v, ci) => (
                  <rect
                    // biome-ignore lint/suspicious/noArrayIndexKey: fixed grid position
                    key={ci}
                    x={48 + ci * 36}
                    y={y}
                    width={32}
                    height={11}
                    fill={PINK}
                    opacity={v}
                  />
                ))}
                {row.score && (
                  <text x={48 + row.score[0] * 36 + 12} y={y + 8} fill="#0c1124" fontWeight={600} fontSize={6}>
                    {row.score[1]}
                  </text>
                )}
              </g>
            );
          })}
        </g>
        <text x={2} y={98} fontFamily={FM} fontSize={5} fill={htwin(GOLD, L)} letterSpacing="0.5">
          GAPS → DEVELOPMENT PRIORITIES
        </text>
      </Canvas>
    </MiniShell>
  );
}

/* ── 06 · GTM — pillar strip + ROI simulator ───────────────────────────── */

const PILLARS: Array<{ label: string; color: string; value: string; sub: string; bars: number[] }> = [
  { label: "DEMAND", color: COBALT, value: "12.4k", sub: "LATENT", bars: [48, 40, 44] },
  { label: "OFFER", color: PURPLE, value: "73%", sub: "MATCH", bars: [48, 42, 38] },
  { label: "MKTG", color: PINK, value: "$14M", sub: "SPEND", bars: [48, 44, 40] },
  { label: "ACCESS", color: GREEN, value: "7/wk", sub: "FLIGHTS", bars: [48, 40, 46] },
];

function SimStage() {
  const [theme] = useTheme();
  const L = theme === "light";
  return (
    <MiniShell label={`UK → ${HERO_DESTINATION.toUpperCase()} · LIVE SIMULATION`} labelColor={GOLD}>
      <Canvas vb="0 0 360 100">
        {PILLARS.map((p, i) => {
          const x = 2 + i * 60;
          return (
            <g key={p.label} transform={`translate(${x} 6)`}>
              <rect width={56} height={88} fill="none" stroke={p.color} strokeWidth={0.6} opacity={0.85} />
              <rect width={56} height={2.5} fill={p.color} />
              <text x={4} y={14} fontFamily={FM} fontSize={5.8} fill={htwin(p.color, L)} letterSpacing="0.5">
                {p.label}
              </text>
              {p.bars.map((bw, bi) => (
                <rect
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed skeleton rows
                  key={bi}
                  x={4}
                  y={22 + bi * 5}
                  width={bw}
                  height={2.5}
                  fill={p.color}
                  opacity={0.9 - bi * 0.15}
                />
              ))}
              <text x={4} y={56} fontFamily={FSERIF} fontStyle="italic" fontSize={14} fill="var(--mk-ink)">
                {p.value}
              </text>
              <text x={4} y={66} fontFamily={FM} fontSize={4.8} fill="var(--mk-dim)" letterSpacing="0.4">
                {p.sub}
              </text>
            </g>
          );
        })}
        <g transform="translate(248 6)">
          <rect width={110} height={88} fill={hexA(GOLD, 0.08)} stroke={GOLD} strokeWidth={0.7} />
          <text x={5} y={11} fontFamily={FM} fontSize={5.2} fill={htwin(GOLD, L)} letterSpacing="0.5">
            SIMULATOR · OUTPUT
          </text>
          <g transform="translate(28 52)">
            <circle r={20} fill="none" stroke={GOLD} strokeWidth={0.5} opacity={0.4} />
            <circle
              r={20}
              fill="none"
              stroke={GOLD}
              strokeWidth={2.4}
              strokeDasharray="92 200"
              strokeDashoffset={32}
              transform="rotate(-90)"
            />
            <text y={-1} textAnchor="middle" fontFamily={FSERIF} fontStyle="italic" fontSize={13} fill="var(--mk-ink)">
              4.6×
            </text>
            <text y={8} textAnchor="middle" fontFamily={FM} fontSize={4.4} fill={htwin(GOLD, L)} letterSpacing="0.5">
              ROI YR3
            </text>
          </g>
          {(
            [
              ["INVEST", "$38M", INK],
              ["VOL +", "84k", GREEN],
              ["SPEND", "$312M", GREEN],
            ] as const
          ).map(([label, value, color], i) => (
            <g key={label} transform={`translate(56 ${26 + i * 23})`} fontFamily={FM} fontSize={5}>
              <text fill="var(--mk-dim)" letterSpacing="0.4">
                {label}
              </text>
              <text
                x={50}
                fill={color === INK ? "var(--mk-ink)" : htwin(color, L)}
                textAnchor="end"
                fontFamily={FSERIF}
                fontStyle="italic"
                fontSize={9.5}
              >
                {value}
              </text>
            </g>
          ))}
        </g>
      </Canvas>
    </MiniShell>
  );
}

/* ── platform icons (section 02 stack) — brand marks from the mockup ───── */

function InstagramIcon() {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
      <defs>
        <linearGradient id="lh-igG" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FED576" />
          <stop offset="26%" stopColor="#F47133" />
          <stop offset="61%" stopColor="#BC3081" />
          <stop offset="100%" stopColor="#4C63D2" />
        </linearGradient>
      </defs>
      <rect x={1.5} y={1.5} width={21} height={21} rx={5.5} fill="url(#lh-igG)" />
      <circle cx={12} cy={12} r={5.2} fill="none" stroke="#fff" strokeWidth={2} />
      <circle cx={17.5} cy={6.5} r={1.3} fill="#fff" />
    </svg>
  );
}

function RedditIcon() {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
      <circle cx={12} cy={12} r={11.5} fill="#FF4500" />
      <line x1={14.6} y1={8.8} x2={17.4} y2={5.2} stroke="#fff" strokeWidth={0.9} strokeLinecap="round" />
      <circle cx={17.6} cy={4.6} r={1.4} fill="#fff" />
      <ellipse cx={12} cy={13.2} rx={5.8} ry={5} fill="#fff" />
      <circle cx={9.6} cy={12.6} r={1.15} fill="#FF4500" />
      <circle cx={14.4} cy={12.6} r={1.15} fill="#FF4500" />
      <circle cx={9.9} cy={12.3} r={0.35} fill="#fff" />
      <circle cx={14.7} cy={12.3} r={0.35} fill="#fff" />
      <path d="M 9.2 14.7 Q 12 16.6 14.8 14.7" stroke="#FF4500" strokeWidth={0.75} fill="none" strokeLinecap="round" />
      <circle cx={7.6} cy={14.2} r={0.55} fill="#FFB59B" />
      <circle cx={16.4} cy={14.2} r={0.55} fill="#FFB59B" />
    </svg>
  );
}

function TripadvisorIcon() {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
      <circle cx={12} cy={12} r={11.5} fill="#00AF87" />
      <circle cx={8} cy={12} r={3.4} fill="#fff" />
      <circle cx={8} cy={12} r={1.55} fill="#000" />
      <circle cx={8.5} cy={11.5} r={0.45} fill="#fff" />
      <circle cx={16} cy={12} r={3.4} fill="#fff" />
      <circle cx={16} cy={12} r={1.55} fill="#000" />
      <circle cx={16.5} cy={11.5} r={0.45} fill="#fff" />
      <path
        d="M 9.5 17 Q 12 18.2 14.5 17"
        stroke="#fff"
        strokeWidth={0.7}
        fill="none"
        strokeLinecap="round"
        opacity={0.85}
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
      <rect width={24} height={24} rx={3} fill="#000" />
      <path
        fill="#fff"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  );
}

/* ── small chrome icons (section 01 headers) ───────────────────────────── */

function GlobeIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
      <circle cx={12} cy={12} r={10} fill="none" stroke={COBALT} strokeWidth={1.2} />
      <ellipse cx={12} cy={12} rx={10} ry={4.2} fill="none" stroke={COBALT} strokeWidth={0.9} opacity={0.7} />
      <ellipse cx={12} cy={12} rx={4.2} ry={10} fill="none" stroke={COBALT} strokeWidth={0.9} opacity={0.7} />
      <line x1={2} y1={12} x2={22} y2={12} stroke={COBALT} strokeWidth={0.9} opacity={0.7} />
    </svg>
  );
}

// The focus country's flag, whichever deployment this is. Shares the artwork
// the rest of the dashboard uses (flag.tsx), so the demo country gets its drawn
// flag here too rather than a two-letter chip.
function FocusFlag() {
  return <Flag iso3={COUNTRY_ISO3} width={18} style={{ display: "block", flexShrink: 0 }} />;
}

/* ── flags (section 04 ranked list) — 36×24 artwork placed via <g> ─────── */

type FlagKind = "uk" | "de" | "us" | "fr" | "in" | "cn" | "it" | "jp";

function FlagG({ kind, x, y, w, h }: { kind: FlagKind; x: number; y: number; w: number; h: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${w / 36} ${h / 24})`}>
      <FlagArt kind={kind} />
    </g>
  );
}

function FlagArt({ kind }: { kind: FlagKind }) {
  switch (kind) {
    case "uk":
      return (
        <g>
          <rect width={36} height={24} fill="#012169" />
          <path d="M 0 0 L 36 24 M 36 0 L 0 24" stroke="#fff" strokeWidth={3} />
          <path d="M 0 0 L 36 24 M 36 0 L 0 24" stroke="#C8102E" strokeWidth={1.5} />
          <path d="M 18 0 L 18 24 M 0 12 L 36 12" stroke="#fff" strokeWidth={5} />
          <path d="M 18 0 L 18 24 M 0 12 L 36 12" stroke="#C8102E" strokeWidth={2.5} />
        </g>
      );
    case "de":
      return (
        <g>
          <rect width={36} height={8} fill="#000" />
          <rect y={8} width={36} height={8} fill="#DD0000" />
          <rect y={16} width={36} height={8} fill="#FFCE00" />
        </g>
      );
    case "us":
      return (
        <g>
          <rect width={36} height={24} fill="#fff" />
          {[0, 4, 8, 12, 16, 20].map((y) => (
            <rect key={y} y={y} width={36} height={2} fill="#B22234" />
          ))}
          <rect width={15} height={12} fill="#3C3B6E" />
        </g>
      );
    case "fr":
      return (
        <g>
          <rect width={12} height={24} fill="#002395" />
          <rect x={12} width={12} height={24} fill="#fff" />
          <rect x={24} width={12} height={24} fill="#ED2939" />
        </g>
      );
    case "in":
      return (
        <g>
          <rect width={36} height={8} fill="#FF9933" />
          <rect y={8} width={36} height={8} fill="#fff" />
          <rect y={16} width={36} height={8} fill="#138808" />
          <circle cx={18} cy={12} r={2.6} fill="none" stroke="#000080" strokeWidth={0.5} />
        </g>
      );
    case "cn":
      return (
        <g>
          <rect width={36} height={24} fill="#EE1C25" />
          <polygon points="6,5 7,8 10,8 7.5,10 8.5,13 6,11 3.5,13 4.5,10 2,8 5,8" fill="#FFFF00" />
        </g>
      );
    case "it":
      return (
        <g>
          <rect width={12} height={24} fill="#009246" />
          <rect x={12} width={12} height={24} fill="#fff" />
          <rect x={24} width={12} height={24} fill="#CE2B37" />
        </g>
      );
    case "jp":
      return (
        <g>
          <rect width={36} height={24} fill="#fff" />
          <circle cx={18} cy={12} r={7} fill="#BC002D" />
        </g>
      );
  }
}

/* Hex + alpha → rgba() string. */
function hexA(hex: string, a: number): string {
  const h = hex.replace("#", "");
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
